/**
 * @file look-warnings.ts
 * @description Предупреждения об оформлении секции — не запреты (phase19.16,
 * партия SB120, этап 1; site-builder.md SB120 «платформа держит закон и
 * доступность как предупреждение», SB100 и SB7 переписаны в предупреждения;
 * проект данных db-design-sb120-stage1.md §4, проект UI uiux-sb120-stage1.md
 * §6). Чистая функция: кабинет показывает «!» у группы панели
 * (issue-groups.ts), публикацию предупреждения не блокируют.
 *
 * Считается по данным, без DOM: телефон — кадр 360×640, ПК — экран 1280
 * (`SECTION_PREVIEW_FRAME`). «Текст выше фото на N px» — замер на канве, не
 * здесь. Размеры файлов — из медиатеки (`width`/`height`; у части SVG их нет —
 * тогда проверка по файлу пропускается).
 */

import { densityFactor, type SiteDensityInput } from "../site-theme/legacy-v1.js";
import { spaceStepPx } from "../site-theme/legacy-steps.js";
import { SECTION_PREVIEW_FRAME, SECTION_WARNING_LIMITS } from "./look-limits.js";
import { isTypoElementKind } from "./element-typo.js";
import {
  type SectionHeight,
  type SectionPayload,
  type SectionSpaceSide,
} from "./schema.js";

export type SectionLookWarningCode =
  | "phone_side_lt12"
  | "phone_taller_than_screen"
  | "width_lt480"
  | "narrow_column"
  | "photo_smaller_than_frame"
  | "photo_band_phone"
  | "text_on_photo";

/** Предупреждение: код, путь поля в payload и число для подписи (px), если есть. */
export interface SectionLookWarning {
  code: SectionLookWarningCode;
  path: string;
  /** px: отступ на телефоне, высота на телефоне, ширина колонки или кадра. */
  value?: number;
}

/** Тексты предупреждений словами владельца (uiux-sb120-stage1 §6, §2.1, §2.5). */
export const SECTION_LOOK_WARNING_MESSAGES: Readonly<Record<SectionLookWarningCode, string>> = {
  phone_side_lt12: "Меньше 12 px по бокам — на телефоне текст прилипнет к краю экрана. Можно оставить.",
  phone_taller_than_screen: "Выше экрана телефона — кнопку увидят только после прокрутки.",
  width_lt480: "Уже 480 px — строки станут короткими.",
  narrow_column: "Узкая колонка — по 2–3 слова в строке.",
  photo_smaller_than_frame: "Фото меньше кадра — может быть размыто.",
  photo_band_phone: "Широкое фото «По фото» на телефоне — низкая полоса, текст не поместится. Лучше кадр 4:5 или «Экран».",
  text_on_photo: "Текст на фото — проверьте на канве (контраст по фото не считается).",
};

/** Размеры файла медиатеки; null — неизвестны (часть SVG). */
export interface SectionMediaSize {
  width: number | null;
  height: number | null;
}

/** Размеры файлов секции по id медиатеки (из карты `media` ответа страниц). */
export type SectionMediaSizes = Readonly<Record<string, SectionMediaSize | undefined>>;

/** Что нужно от темы: плотность (поля сетки, «как у сайта») и доля телефона. */
export interface SectionWarningTheme {
  spacing: { density: SiteDensityInput; phoneFactor: number };
}

/** Боковые поля сетки «как у сайта» на ПК (globals.css `.px-section` ≥ 1024: 70 × плотность). */
const SITE_SIDE_DESKTOP = 70;

const { phone: PHONE, desktop: DESKTOP } = SECTION_PREVIEW_FRAME;
const W = SECTION_WARNING_LIMITS;

const round = (v: number): number => Math.round(v);

/** Пропорции файла (ширина / высота); null — неизвестны. */
function fileAspect(id: string | undefined, media: SectionMediaSizes): number | null {
  const size = id ? media[id] : undefined;
  if (!size || !size.width || !size.height) return null;
  return size.width / size.height;
}

/** Ширина колонок на ПК при экране 1280, px. */
function desktopColumnWidths(payload: SectionPayload, density: SiteDensityInput): number[] {
  const { look, columns } = payload;
  const full = look.container === "full";
  const width = typeof look.container === "number" ? Math.min(look.container, DESKTOP.width) : DESKTOP.width;
  const siteSide = full ? 0 : SITE_SIDE_DESKTOP * densityFactor(density);
  const sides = (look.space.innerLeft ?? siteSide) + (look.space.innerRight ?? siteSide);
  const gap = columns.gap ?? spaceStepPx("m", density);
  const count = columns.items.length;
  const content = Math.max(0, width - sides - gap * (count - 1));
  return columns.shares.map((share) => (content * share) / 12);
}

/** Высота на телефоне, px; null — по содержимому или неизвестна. */
function phoneHeight(height: SectionHeight, aspect: number | null): number | null {
  switch (height.kind) {
    case "number":
      return height.value;
    case "photo": {
      const ratio = height.aspect ?? aspect;
      return ratio ? PHONE.width / ratio : null;
    }
    case "screen":
      return ((PHONE.height - PHONE.header) * height.value) / 100;
    default:
      return null;
  }
}

/**
 * Предупреждения об оформлении секции. Порядок — порядок групп панели:
 * ширина и высота, отступы, колонки, фон и фото.
 */
export function sectionLookWarnings(
  payload: SectionPayload,
  theme: SectionWarningTheme,
  media: SectionMediaSizes = {},
): SectionLookWarning[] {
  const out: SectionLookWarning[] = [];
  const { look, columns } = payload;
  const { density } = theme.spacing;
  const bg = look.bg.kind === "media" ? look.bg : null;

  // Ширина и высота.
  if (typeof look.container === "number" && look.container < W.containerMin) {
    out.push({ code: "width_lt480", path: "look.container", value: look.container });
  }
  const phoneMode = look.heightPhone ?? look.height;
  const phonePath = look.heightPhone ? "look.heightPhone" : "look.height";
  const phoneAspect = bg ? fileAspect(bg.mediaIdPhone ?? bg.mediaId, media) : null;
  const phonePx = phoneHeight(phoneMode, phoneAspect);
  if (phonePx !== null && phonePx > PHONE.height) {
    out.push({ code: "phone_taller_than_screen", path: phonePath, value: round(phonePx) });
  }
  if (phoneMode.kind === "photo" && phonePx !== null && phonePx < W.photoBandMin) {
    out.push({ code: "photo_band_phone", path: phonePath, value: round(phonePx) });
  }

  // Отступы по бокам на телефоне — только явные: своё число телефона или ПК × доля.
  // «Как у сайта» (нет ключа) не проверяем, даже при «Во всю»: поля сетки даёт витрина.
  const factor = look.space.phoneFactor ?? theme.spacing.phoneFactor;
  for (const side of ["innerLeft", "innerRight"] as const satisfies readonly SectionSpaceSide[]) {
    const own = look.space.phone?.[side];
    const desktop = look.space[side];
    const value = own ?? (desktop !== undefined ? desktop * factor : undefined);
    if (value !== undefined && value < W.phoneSideMin) {
      out.push({
        code: "phone_side_lt12",
        path: own !== undefined ? `look.space.phone.${side}` : `look.space.${side}`,
        value: Math.round(value * 10) / 10,
      });
    }
  }

  // Колонки: узкие и фото меньше колонки.
  const widths = desktopColumnWidths(payload, density);
  if (columns.items.length > 1) {
    widths.forEach((w, i) => {
      if (w < W.narrowColumnPx) out.push({ code: "narrow_column", path: `columns.shares.${i}`, value: round(w) });
    });
  }
  columns.items.forEach((column, col) => {
    column.elements.forEach((el, row) => {
      if (el.kind !== "image" || el.mediaId === null) return;
      const width = media[el.mediaId]?.width;
      const frame = widths[col] ?? 0;
      if (width && width < frame) {
        out.push({ code: "photo_smaller_than_frame", path: `columns.items.${col}.elements.${row}.mediaId`, value: round(frame) });
      }
    });
  });

  // Фото фона: кадр ПК (экран × масштаб) и телефона, текст на фото.
  if (bg) {
    const desktopFrame = DESKTOP.width * (bg.zoom ?? 1);
    const desktopWidth = media[bg.mediaId]?.width;
    if (desktopWidth && desktopWidth < desktopFrame) {
      out.push({ code: "photo_smaller_than_frame", path: "look.bg.mediaId", value: round(desktopFrame) });
    }
    if (bg.mediaIdPhone) {
      const phoneFrame = PHONE.width * (bg.zoomPhone ?? bg.zoom ?? 1);
      const phoneWidth = media[bg.mediaIdPhone]?.width;
      if (phoneWidth && phoneWidth < phoneFrame) {
        out.push({ code: "photo_smaller_than_frame", path: "look.bg.mediaIdPhone", value: round(phoneFrame) });
      }
    }
    // Текст — у видов с частями `typo` (v4; прежний список «Цвет текста» — их подмножество).
    const hasText = payload.header !== undefined || columns.items.some((c) => c.elements.some((e) => isTypoElementKind(e.kind)));
    if (hasText) out.push({ code: "text_on_photo", path: "look.bg.textColor" });
  }
  return out;
}
